import React, { memo, useCallback, useEffect, useState } from 'react'
import { BdaServiceApplicationProperty, BdaServiceProperty, BdaServiceTuningProperty, TuningPropertyMapping } from '../../models/BdaService';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Button } from 'react-bootstrap';
import { Checkbox, CheckboxChangeEvent, Flex, Form, Input, InputNumber, Modal, Radio, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MappingsEditor } from './MappingsEditor';
import { getPropertyIcon, setObjectType } from '../../utils';
import ScriptEditor from '../common/ScriptEditor';
import { Option } from '../../models/generics';
import { Models } from '../../api/Models';
import { useToasts } from '../../contexts/toasts';
import { isAssetProperty } from '../../utils/DesignerUtils';
import IconButton from '../common/IconButton';

const types = [
  { label: 'Application Property', value: 'application' },
  { label: 'Tuning Property', value: 'tuning' },
  { label: 'Static Property', value: 'static' },
].map(type => ({ ...type, icon: getPropertyIcon(type.value as "application" | "tuning" | "static") }));

const renderPresetValueInput = (valueType: BdaServiceProperty['valueType']) => {
  switch (valueType) {
    case 'INT':
    case 'DOUBLE': return <InputNumber className="w-100" placeholder="Preset value" precision={valueType === "INT" ? 0 : 2} step={valueType === "DOUBLE" ? 0.01 : undefined} />;
    default: return <Input placeholder="Preset value" />;
  }
}

const renderJSONPresetFormItem = (field: any) => {
  return (
    <div className="w-100">
      <Form.Item name={[field.name, "label"]}>
        <Input placeholder="Preset Label" />
      </Form.Item>
      <Form.Item name={[field.name, "value"]}>
        <ScriptEditor height={200} />
      </Form.Item>
    </div>
  );
}

type ModalProps = {
  open: boolean;
  onClose: VoidFunction;
  property: BdaServiceProperty;
  onSubmit: (values: any) => void;
}

const ServicePropertyModal = memo(({ open, onClose, onSubmit, property }: ModalProps) => {
  const [form] = Form.useForm();
  const { showToast } = useToasts();
  const [mappings, setMappings] = useState<TuningPropertyMapping[]>();
  const [defaultValue, setDefaultValue] = useState<string | undefined>();
  const [formatOptions, setFormatOptions] = useState<Option[]>();

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(property);
    form.setFieldValue("presetValues", []);
    if (property.type === "application" && setObjectType<BdaServiceApplicationProperty>(property)) {
      if (property.inputData) form.setFieldValue("dataType", "inputData");
      if (property.outputData) form.setFieldValue("dataType", "outputData");
      if (property.valueType === "JSON") {
        property.presetValues?.forEach((presetString, index) => {
          const preset = JSON.parse(presetString as string);
          form.setFieldValue(["presetValues", index, "key"], preset.key);
          form.setFieldValue(["presetValues", index, "label"], preset.label);
          form.setFieldValue(["presetValues", index, "value"], preset.value);
        });
      }
      if (property.extra?.mode === 'streaming') form.setFieldValue("streaming", true);
      if (property.model?.format) {
        form.setFieldValue("modelServing", true);
        form.setFieldValue("modelFormat", property.model?.format);
      };
    }
    if (property.type === "tuning") setMappings((property as BdaServiceTuningProperty).mappings);
    if (property.valueType === "JSON") setDefaultValue(property.defaultValue || "");
  }, [form, property, showToast]);

  const handleMappingsChange = (newMappings: TuningPropertyMapping[]) => setMappings(newMappings);

  const handleSubmit = (values: any) => {
    if (values.type === 'tuning') values.mappings = mappings || [];
    if (values.valueType === 'JSON') {
      values.defaultValue = defaultValue;
      if (values.presetValues) {
        values.presetValues = values.presetValues.map((preset: any, index: number) => {
          if (typeof preset.value === "string") preset.value = JSON.parse(preset.value);
          return JSON.stringify({ key: index, ...preset });
        });
      }
    }
    if (values.valueType === 'BOOLEAN') delete values.presetValues;
    if (values.streaming) values.extra = { mode: 'streaming' };
    if (values.dataType) {
      values.inputData = values.dataType === "inputData";
      values.outputData = values.dataType === "outputData";
    }
    if (values.modelServing) {
      values.model = {
        format: values.modelFormat,
      };
    }
    delete values.dataType;
    delete values.streaming;
    delete values.modelServing;
    delete values.modelFormat;
    onSubmit(values);
  }

  const handleDefaultValueChange = useCallback((value?: string) => setDefaultValue(value || ""), []);

  const getFormats = useCallback(() => {
    Models.getModelsFormats()
      .then(
        ({ data }) => setFormatOptions(data.map(format => ({ label: format, value: format }))),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          setFormatOptions([]);
          showToast('formats-error', message, `Formats Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast]);

  const handleModelServingChange = useCallback((e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    if (!checked) form.setFieldValue("modelFormat", undefined);
    else if (!formatOptions) getFormats();
  }, [form, formatOptions, getFormats]);

  const handleValueTypeChange = useCallback(() => form.setFieldValue("presetValues", []), [form]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onClose={onClose}
      style={{ top: 20 }}
      title="Edit Service Property"
      footer={null}
    >
      <Form form={form} layout="vertical" colon={false} onFinish={handleSubmit} className="compact-form mt-3">
        <Form.Item
          name="key"
          label="Key"
          rules={[{ required: true, message: "Property Key is required" }]}
        >
          <Input placeholder="Property key" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea placeholder="Property description" rows={3} />
        </Form.Item>

        <Form.Item name="type" label="Type">
          <Select className="w-100" placeholder="Select Property Type">
            {types.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <FontAwesomeIcon icon={option.icon} fixedWidth />
                <span className="ms-2">{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="valueType" label="Value Type">
          <Select className="w-100" placeholder="Select Property Value Type" onChange={handleValueTypeChange}>
            <Select.Option value="STRING">String</Select.Option>
            <Select.Option value="BOOLEAN">Boolean</Select.Option>
            <Select.Option value="INT">Integer</Select.Option>
            <Select.Option value="DOUBLE">Double</Select.Option>
            <Select.Option value="JSON">JSON</Select.Option>
          </Select>
        </Form.Item>

        <Flex align="center">
          <Form.Item name="mandatory" valuePropName="checked">
            <Checkbox>Mandatory</Checkbox>
          </Form.Item>

          <Form.Item className="ms-3" name="invisible" valuePropName="checked">
            <Checkbox>Invisible</Checkbox>
          </Form.Item>

          <Form.Item noStyle dependencies={['valueType']}>
            {({ getFieldValue }) => {
              const valueType = getFieldValue('valueType') as BdaServiceProperty['valueType'];

              return (valueType === 'JSON' || valueType === 'STRING') && (
                <Form.Item className="ms-3" name="obfuscated" valuePropName="checked">
                  <Checkbox>Obfuscated</Checkbox>
                </Form.Item>
              )
            }}
          </Form.Item>
        </Flex>

        <Form.Item noStyle dependencies={['valueType']}>
          {({ getFieldValue }) => {
            const valueType = getFieldValue('valueType') as BdaServiceProperty['valueType'];
            if (valueType === 'BOOLEAN') return (
              <Form.Item name="defaultValue" valuePropName="checked">
                <Checkbox>Default Value</Checkbox>
              </Form.Item>
            )
            else if (valueType === 'JSON') return (
              <>
                <h6 className="mb-1">Default Value</h6>
                <ScriptEditor height={300} className="mb-2" value={defaultValue} onChange={handleDefaultValueChange} />
              </>
            )
            else return (
              <Form.Item name="defaultValue" label="Default Value">
                <Input placeholder="Property default value" step={valueType === 'DOUBLE' ? 0.01 : 1} />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item noStyle dependencies={['valueType']}>
          {({ getFieldValue }) => {
            const valueType = getFieldValue('valueType') as BdaServiceProperty['valueType'];

            return valueType && valueType !== 'BOOLEAN' && (
              <Form.List name="presetValues">
                {(fields, { add, remove }) => (
                  <>
                    <Flex align="center" justify="space-between" className="mb-2">
                      <h6 className="mb-0">Preset Values</h6>
                      <Button variant="light" type="button" size="sm" className="border" onClick={() => add()}>
                        <FontAwesomeIcon icon={solid('plus')} />
                        <span className="ms-2">Add Value</span>
                      </Button>
                    </Flex>
                    {fields.map(({ key, ...field }) => (
                      <Flex key={key} align="flex-start" gap={8}>
                        {valueType === "JSON"
                          ? renderJSONPresetFormItem(field)
                          : (
                            <Form.Item
                              {...field}
                              className="flex-grow-1"
                              rules={[{ required: true, message: "Preset value is required" }]}
                            >
                              {renderPresetValueInput(valueType)}
                            </Form.Item>
                          )
                        }
                        <IconButton className="p-1" type="button" onClick={() => remove(field.name)}>
                          <FontAwesomeIcon icon={solid('times')} />
                        </IconButton>
                      </Flex>
                    ))}
                  </>
                )}
              </Form.List>
            )
          }}
        </Form.Item>

        <Form.Item noStyle dependencies={['type', 'key']}>
          {({ getFieldValue }) => {
            const type: "application" | "tuning" | "static" = getFieldValue('type');
            const key = getFieldValue('key');

            return type === "application"
              ? (
                <>
                  <Form.Item name="dataType" label="Data Type" className="w-50 no-padding">
                    <Radio.Group
                      options={[
                        { label: "Input Data", value: "inputData" },
                        { label: "Output Data", value: "outputData" },
                      ]}
                    />
                  </Form.Item>

                  {isAssetProperty(key) && (
                    <Form.Item name="streaming" valuePropName="checked">
                      <Checkbox>Streaming</Checkbox>
                    </Form.Item>
                  )}

                  {isAssetProperty(key, 'model') && (
                    <Flex align="center" justify="flex-start">
                      <Form.Item name="modelServing" valuePropName="checked">
                        <Checkbox onChange={handleModelServingChange}>Model Serving</Checkbox>
                      </Form.Item>

                      <Form.Item noStyle dependencies={['modelServing']}>
                        {({ getFieldValue }) => {
                          const serving = getFieldValue('modelServing');
                          return serving && (
                            <Form.Item name="modelFormat" label="Format" layout="horizontal" className="w-50 ms-3 no-padding">
                              <Select options={formatOptions} placeholder="Model Format" />
                            </Form.Item>
                          )
                        }}
                      </Form.Item>
                    </Flex>
                  )}
                </>
              )
              : type === "tuning"
                ? (
                  <>
                    <Form.Item name="measure" label="Measure">
                      <Input placeholder="Property Measure" />
                    </Form.Item>

                    <Form.Item name="category" valuePropName="checked">
                      <Checkbox>Category</Checkbox>
                    </Form.Item>

                    <Form.Item name="minValue" label="Min Value">
                      <Input placeholder="Property Min Value" />
                    </Form.Item>

                    <Form.Item name="maxValue" label="Max Value">
                      <Input placeholder="Property Max Value" />
                    </Form.Item>

                    <h6>Mappings</h6>
                    <MappingsEditor mappings={mappings || []} onMappingsChange={handleMappingsChange} />
                  </>
                )
                : (
                  <>
                    {/* <Form.Item name="externalized" valuePropName="checked">
                      <Checkbox>Externalized</Checkbox>
                    </Form.Item>

                    <Form.Item noStyle dependencies={['externalized']}>
                      {({ getFieldValue }) => getFieldValue('externalized') && (
                        <Form.Item name="url" label="Url" rules={[{ required: true, message: "Property URL is required when property is externalized" }]}>
                          <Input placeholder="Property URL" />
                        </Form.Item>
                      )}
                    </Form.Item> */}
                  </>
                )
          }}
        </Form.Item>

        <Flex align="center" justify="space-between" className="mt-3">
          <Button variant="light" type="button" size="sm" className="border" onClick={onClose}>Close</Button>
          <Button variant="success" type="submit" size="sm">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
})

export default ServicePropertyModal;